import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { 
  LayoutDashboard, Users, Trophy, Calendar, 
  UtensilsCrossed, Settings, LogOut, Menu, X,
  Shuffle, Award, Image, LayoutGrid, Handshake, Target, Camera
} from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '../../lib/auth';
import { cn } from '../../lib/utils';
import { Button } from '../../components/ui/button';

const navItems = [
  { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
  { path: '/admin/squadre', icon: Users, label: 'Squadre', adminOnly: true },
  { path: '/admin/sorteggio', icon: Shuffle, label: 'Sorteggio', adminOnly: true },
  { path: '/admin/partite', icon: Calendar, label: 'Partite Gironi' },
  { path: '/admin/eliminatorie', icon: Award, label: 'Eliminatorie' },
  { path: '/admin/capocannoniere', icon: Target, label: 'Capocannoniere' },
  { path: '/admin/tabellone', icon: LayoutGrid, label: 'Tabellone' },
  { path: '/admin/giocatori', icon: Trophy, label: 'Giocatori' },
  { path: '/admin/albo-doro', icon: Award, label: "Albo d'Oro" },
  { path: '/admin/galleria', icon: Camera, label: 'Galleria foto' },
  { path: '/admin/sponsor', icon: Handshake, label: 'Sponsor', adminOnly: true },
  { path: '/admin/menu', icon: UtensilsCrossed, label: 'Menu', adminOnly: true },
  { path: '/admin/logo', icon: Image, label: 'Logo', adminOnly: true },
  { path: '/admin/impostazioni', icon: Settings, label: 'Impostazioni', adminOnly: true },
];

export const AdminLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const isAdmin = user?.role === 'admin';
  const visibleNavItems = navItems.filter(item => isAdmin || !item.adminOnly);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-slate-950" data-testid="admin-layout">
      {/* Mobile Header */}
      <header className="lg:hidden sticky top-0 z-50 bg-slate-900 border-b border-slate-800 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Trophy className="w-6 h-6 text-primary" />
          <span className="font-heading font-bold">ADMIN</span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          data-testid="mobile-menu-toggle"
        >
          {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside 
          className={cn(
            "fixed lg:sticky top-0 left-0 z-40 h-screen w-64 bg-slate-900 border-r border-slate-800 transition-transform lg:translate-x-0 flex flex-col",
            sidebarOpen ? "translate-x-0" : "-translate-x-full"
          )}
        >
          {/* Logo */}
          <div className="hidden lg:flex items-center gap-3 px-6 py-5 border-b border-slate-800">
            <Trophy className="w-8 h-8 text-primary" />
            <div>
              <h1 className="font-heading font-bold">ADMIN PANEL</h1>
              <p className="text-xs text-gold">
                {isAdmin ? '30ª Edizione' : `Operatore · ${user?.username || ''}`}
              </p>
            </div>
          </div>

          {/* Nav Items */}
          <nav className="p-4 space-y-1 mt-16 lg:mt-0 flex-1 overflow-y-auto">
            {visibleNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={cn(
                  "flex items-center gap-3 px-4 py-3 rounded-lg transition-colors",
                  isActive(item.path, item.exact)
                    ? "bg-primary/10 text-primary"
                    : "text-slate-400 hover:bg-slate-800 hover:text-white"
                )}
                data-testid={`admin-nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </Link>
            ))}
          </nav>

          {/* Footer */}
          <div className="mt-auto p-4 border-t border-slate-800">
            <Link
              to="/"
              className="flex items-center gap-3 px-4 py-3 text-slate-400 hover:text-white transition-colors"
              data-testid="admin-go-to-site"
            >
              <Trophy className="w-5 h-5" />
              <span>Vai al sito</span>
            </Link>
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-3 w-full text-red-400 hover:text-red-300 transition-colors"
              data-testid="admin-logout"
            >
              <LogOut className="w-5 h-5" />
              <span>Esci</span>
            </button>
          </div>
        </aside>

        {/* Overlay */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 min-h-screen min-w-0 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
